import { useEffect } from "react";
import APPLICATION_GLOBAL_CONFIG from "app/config/applicationGlobalConfig";

const manifestSelector = 'link[rel="manifest"]';
const defaultHrefAttribute = "data-primum-default-href";

type WindowWithIdleCallback = Window & typeof globalThis & {
    requestIdleCallback?: (callback: () => void, options?: { timeout?: number }) => number;
    cancelIdleCallback?: (handle: number) => void;
};

const publicUrl = process.env.PUBLIC_URL || "";
const globalManifestHref = `${publicUrl}/manifest.json`;
const apiBase = APPLICATION_GLOBAL_CONFIG.apiBase || "";

const manifestHrefForGate = (slug: string) => `${apiBase}/api/gates/${encodeURIComponent(slug)}/manifest.json`;

export const resolveGateManifestSlug = (routeSlug?: string, resolvedSlug?: string) => {
    const normalizedRouteSlug = String(routeSlug || "").trim();
    if (normalizedRouteSlug) return normalizedRouteSlug;

    const normalizedResolvedSlug = String(resolvedSlug || "").trim();
    return normalizedResolvedSlug || undefined;
};

const shouldUseCrossOrigin = (href: string) => {
    if (!href || href.startsWith("/")) {
        return false;
    }

    try {
        return new URL(href, window.location.origin).origin !== window.location.origin;
    } catch (error) {
        return false;
    }
};

const ensureManifestLink = () => {
    let link = document.querySelector<HTMLLinkElement>(manifestSelector);

    if (!link) {
        link = document.createElement("link");
        link.rel = "manifest";
        link.href = globalManifestHref;
        document.head.appendChild(link);
    }

    if (!link.getAttribute(defaultHrefAttribute)) {
        link.setAttribute(defaultHrefAttribute, link.getAttribute("href") || globalManifestHref);
    }

    return link;
};

const scheduleManifestUpdate = (callback: () => void) => {
    const browserWindow = window as WindowWithIdleCallback;

    if (typeof browserWindow.requestIdleCallback === "function") {
        const handle = browserWindow.requestIdleCallback(callback, { timeout: 1000 });
        return () => browserWindow.cancelIdleCallback?.(handle);
    }

    const handle = window.setTimeout(callback, 0);
    return () => window.clearTimeout(handle);
};

export const useGatePwaManifest = (slug?: string) => {
    useEffect(() => {
        if (!slug) {
            return;
        }

        let cancelled = false;
        const gateManifestHref = manifestHrefForGate(slug);
        const cancelScheduledUpdate = scheduleManifestUpdate(() => {
            if (cancelled) return;

            const link = ensureManifestLink();
            link.href = gateManifestHref;
            link.crossOrigin = shouldUseCrossOrigin(gateManifestHref) ? "anonymous" : "";
        });

        return () => {
            cancelled = true;
            cancelScheduledUpdate();

            const currentLink = document.querySelector<HTMLLinkElement>(manifestSelector);

            if (!currentLink) {
                return;
            }

            const currentHref = currentLink.getAttribute("href") || "";
            const currentUrl = new URL(currentHref, window.location.origin);
            const gateUrl = new URL(gateManifestHref, window.location.origin);

            if (currentUrl.pathname !== gateUrl.pathname) {
                return;
            }

            currentLink.href = currentLink.getAttribute(defaultHrefAttribute) || globalManifestHref;
            currentLink.crossOrigin = "";
        };
    }, [slug]);
};
